/**
 * Internationalization (i18n) system for the Outlook add-in
 * Automatically detects language from Office.context.displayLanguage
 */

import { en, type Translations } from "./locales/en";
import { fr } from "./locales/fr";
import { it } from "./locales/it";
import { de } from "./locales/de";
import { es } from "./locales/es";

export type SupportedLocale = "en" | "fr" | "it" | "de" | "es";

const translations: Record<SupportedLocale, Translations> = {
  en,
  fr,
  it,
  de,
  es,
};

const supportedLocales: SupportedLocale[] = ["en", "fr", "it", "de", "es"];

function matchLocale(lang: string): SupportedLocale | null {
  const prefix = lang.slice(0, 2);
  return supportedLocales.includes(prefix as SupportedLocale) ? (prefix as SupportedLocale) : null;
}

/**
 * Detects the user's language from Office/Outlook settings
 * Falls back to browser language, then to English
 */
export function detectLanguage(): SupportedLocale {
  // Try Office.context.displayLanguage first (e.g., "en-US", "fr-FR")
  if (typeof Office !== "undefined" && Office.context?.displayLanguage) {
    const officeLanguage = Office.context.displayLanguage.toLowerCase();
    const officeLocale = matchLocale(officeLanguage);
    if (officeLocale) return officeLocale;
  }

  // Fallback to browser language
  if (typeof navigator !== "undefined" && navigator.language) {
    const browserLanguage = navigator.language.toLowerCase();
    const browserLocale = matchLocale(browserLanguage);
    if (browserLocale) return browserLocale;
  }

  // Default to English
  return "en";
}

let currentLocale: SupportedLocale = "en";

/**
 * Initialize the i18n system - call this after Office.onReady()
 */
export function initI18n(): SupportedLocale {
  currentLocale = detectLanguage();
  return currentLocale;
}

/**
 * Get the current locale
 */
export function getLocale(): SupportedLocale {
  return currentLocale;
}

/**
 * Set the locale manually (useful for testing)
 */
export function setLocale(locale: SupportedLocale): void {
  currentLocale = locale;
}

/**
 * Get translations for the current locale
 */
export function getTranslations(): Translations {
  return translations[currentLocale];
}

/**
 * Get a specific translation by path
 * Example: t("ui.addRoom") returns "Add Room" or "Ajouter la salle"
 */
export function t<K extends keyof Translations>(
  category: K
): Translations[K];
export function t<K extends keyof Translations, K2 extends keyof Translations[K]>(
  category: K,
  key: K2
): Translations[K][K2];
export function t(category: string, key?: string): unknown {
  const trans = translations[currentLocale];
  const categoryObj = trans[category as keyof Translations];

  if (key === undefined) {
    return categoryObj;
  }

  return (categoryObj as Record<string, unknown>)[key];
}

export function labelColon(): string {
  return " :";
}

export function capitalize(value: string): string {
  return value ? value.charAt(0).toUpperCase() + value.slice(1) : value;
}

export type { Translations };
export { en, fr, it, de, es };
